import { ButtonHTMLAttributes, FC } from 'react';
import cn from 'classnames';
import { includesCN } from 'src/shared/lib/utils';
import { ToolTip } from 'src/shared/ui';

import {
  ButtonSizesObjType,
  ButtonSizesType,
  ButtonVariablesObjType,
  ButtonVariablesType,
  ButtonVariantsObjType,
  ButtonVariantsType,
} from './types';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tooltip?: string;
  variant?: ButtonVariantsType;
  variable?: ButtonVariablesType;
  size?: ButtonSizesType;
  classNames?: string;
}

export const Button: FC<ButtonProps> = ({
  tooltip,
  variant = 'blue',
  variable = 'text',
  size = 'base',
  classNames,
  children,
  disabled,
  ...props
}) => {
  const variants: ButtonVariantsObjType = {
    blue: 'text-white bg-blue-100 hover:bg-blue-700',
    blueBorder: 'text-white bg-blue-400 border border-blue-300 hover:bg-blue-200',
    lightBlue: 'text-white bg-blue-300 hover:bg-blue-700',
    grey: 'text-white bg-grey-100 hover:bg-blue-400',
    greyLight: 'text-white bg-grey-1400 hover:bg-blue-400',
    orange: 'text-white bg-orange-100 hover:bg-orange-200',
    violet: cn({
      'bg-grey-600': Boolean(disabled),
      'bg-violet-100 hover:bg-violet-200 text-white': !Boolean(disabled),
    }),
    violetOutline: cn({
      'bg-grey-600': Boolean(disabled),
      'hover:bg-grey-600 bg-grey-1200 border-violet-1900 text-blue-1300': !Boolean(disabled),
    }),
    violetDark: cn({
      'bg-grey-600': Boolean(disabled),
      'bg-violet-1000 border border-violet-900 text-grey-700 hover:text-white': !Boolean(disabled),
      'hover:shadow-pink-xl': !Boolean(disabled),
    }),
    green: cn({
      'bg-grey-600': Boolean(disabled),
      'bg-green-200 hover:bg-green-300 text-white': !Boolean(disabled),
    }),
  };

  const variables: ButtonVariablesObjType = {
    text: 'px-5',
    icon: 'aspect-square flex items-center justify-center',
  };

  const sizes: ButtonSizesObjType = {
    base: cn(
      { 'w-full': !includesCN(classNames, 'w-') },
      'rounded flex items-center justify-center h-11',
    ),
    small: cn(
      { 'w-full': !includesCN(classNames, 'w-') },
      'rounded flex items-center justify-center h-8 text-xs',
    ),
  };

  const button = (
    <button
      className={cn(
        'font-gothampro-400 whitespace-nowrap border border-transparent transition-all',
        variants[variant],
        variables[variable],
        sizes[size],
        classNames,
      )}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );

  return tooltip ? <ToolTip label={tooltip}>{button}</ToolTip> : button;
};
